import { useState } from 'react'
import { Plus, Trash2, Edit2, Play, Copy, AlertCircle, CheckCircle, Clock, Zap, Code, RefreshCw, Database, Check, X } from 'lucide-react'

export default function BackendAutomationPage() {
  const [activeTab, setActiveTab] = useState('webhooks') // webhooks, cronjobs, functions, databases
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingId, setEditingId] = useState(null)

  const [webhooks, setWebhooks] = useState([
    { id: 1, name: 'Facebook Post Webhook', url: 'https://api.cybertection.com/webhooks/facebook', method: 'POST', status: 'Active', lastTriggered: '2 mins ago', triggers: 3 },
    { id: 2, name: 'Email Notification Webhook', url: 'https://api.cybertection.com/webhooks/email', method: 'POST', status: 'Active', lastTriggered: '15 mins ago', triggers: 12 },
    { id: 3, name: 'Analytics Update Webhook', url: 'https://api.cybertection.com/webhooks/analytics', method: 'PUT', status: 'Active', lastTriggered: '1 hour ago', triggers: 5 },
  ])

  const [cronjobs, setCronjobs] = useState([
    { id: 1, name: 'Daily Report Generator', schedule: '0 8 * * *', function: 'generateDailyReport', status: 'Active', nextRun: '2024-02-20 08:00 AM', lastRun: '2024-02-19 08:00 AM', runs: 24 },
    { id: 2, name: 'Backup Database', schedule: '0 2 * * 0', function: 'backupDatabase', status: 'Active', nextRun: '2024-02-25 02:00 AM', lastRun: '2024-02-18 02:00 AM', runs: 4 },
    { id: 3, name: 'Clean Old Logs', schedule: '0 3 * * *', function: 'cleanOldLogs', status: 'Active', nextRun: '2024-02-20 03:00 AM', lastRun: '2024-02-19 03:00 AM', runs: 18 },
  ])

  const [functions, setFunctions] = useState([
    { id: 1, name: 'generateDailyReport', trigger: 'Webhook + Cron', status: 'Active', code: 'async function generateDailyReport() { /* report logic */ }', lastExecuted: '2024-02-19 08:00 AM', executions: 24 },
    { id: 2, name: 'sendEmailNotification', trigger: 'Webhook', status: 'Active', code: 'async function sendEmailNotification(email, subject) { /* email logic */ }', lastExecuted: '2024-02-19 02:30 PM', executions: 89 },
    { id: 3, name: 'backupDatabase', trigger: 'Cron', status: 'Active', code: 'async function backupDatabase() { /* backup logic */ }', lastExecuted: '2024-02-18 02:00 AM', executions: 4 },
  ])

  const [databases, setDatabases] = useState([
    { id: 1, name: 'Cybertection Main DB', type: 'PostgreSQL', host: 'db.cybertection.com', port: 5432, database: 'cybertection_prod', status: 'Connected', lastConnection: '1 min ago', collections: 45 },
    { id: 2, name: 'Firebase Project', type: 'Firebase', projectId: 'cybertection-firebase', region: 'us-central1', status: 'Connected', lastConnection: '5 mins ago', collections: 12 },
    { id: 3, name: 'Analytics DB', type: 'MongoDB', host: 'mongo.cybertection.com', port: 27017, database: 'analytics', status: 'Connected', lastConnection: '10 mins ago', collections: 8 },
  ])

  const [formData, setFormData] = useState({
    name: '',
    url: '',
    method: 'POST',
    schedule: '',
    function: '',
    code: '',
    trigger: 'Webhook',
    dbType: 'PostgreSQL',
    dbHost: '',
    dbPort: '',
    dbName: '',
    dbUser: '',
    dbPassword: '',
    dbProjectId: '',
    dbRegion: '',
    connectionString: '',
  })

  const dbTypes = [
    { name: 'PostgreSQL', icon: '🐘', port: 5432 },
    { name: 'MySQL', icon: '🐬', port: 3306 },
    { name: 'MariaDB', icon: '🗄️', port: 3306 },
    { name: 'MongoDB', icon: '🍃', port: 27017 },
    { name: 'Firebase', icon: '🔥', port: null },
    { name: 'SQLServer', icon: '💾', port: 1433 },
    { name: 'Oracle', icon: '⚙️', port: 1521 },
    { name: 'Cassandra', icon: '📊', port: 9042 },
  ]

  const handleAddItem = () => {
    if (activeTab === 'webhooks' && formData.name && formData.url) {
      if (editingId) {
        setWebhooks(webhooks.map(w => w.id === editingId ? { ...w, name: formData.name, url: formData.url, method: formData.method } : w))
      } else {
        setWebhooks([...webhooks, { id: Date.now(), name: formData.name, url: formData.url, method: formData.method, status: 'Active', lastTriggered: 'Never', triggers: 0 }])
      }
    } else if (activeTab === 'cronjobs' && formData.name && formData.schedule && formData.function) {
      if (editingId) {
        setCronjobs(cronjobs.map(c => c.id === editingId ? { ...c, name: formData.name, schedule: formData.schedule, function: formData.function } : c))
      } else {
        setCronjobs([...cronjobs, { id: Date.now(), name: formData.name, schedule: formData.schedule, function: formData.function, status: 'Active', nextRun: 'Calculating...', lastRun: 'Never', runs: 0 }])
      }
    } else if (activeTab === 'functions' && formData.name && formData.code) {
      if (editingId) {
        setFunctions(functions.map(f => f.id === editingId ? { ...f, name: formData.name, code: formData.code, trigger: formData.trigger } : f))
      } else {
        setFunctions([...functions, { id: Date.now(), name: formData.name, trigger: formData.trigger, status: 'Active', code: formData.code, lastExecuted: 'Never', executions: 0 }])
      }
    } else if (activeTab === 'databases' && formData.name && formData.dbType) {
      if (editingId) {
        setDatabases(databases.map(d => d.id === editingId ? { ...d, name: formData.name, type: formData.dbType, host: formData.dbHost, port: formData.dbPort, database: formData.dbName } : d))
      } else {
        setDatabases([...databases, { id: Date.now(), name: formData.name, type: formData.dbType, host: formData.dbHost, port: formData.dbPort, database: formData.dbName, status: 'Connected', lastConnection: 'Just now', collections: 0 }])
      }
    }
    setFormData({ name: '', url: '', method: 'POST', schedule: '', function: '', code: '', trigger: 'Webhook', dbType: 'PostgreSQL', dbHost: '', dbPort: '', dbName: '', dbUser: '', dbPassword: '', dbProjectId: '', dbRegion: '', connectionString: '' })
    setShowAddForm(false)
    setEditingId(null)
  }

  const handleDelete = (id) => {
    if (activeTab === 'webhooks') setWebhooks(webhooks.filter(w => w.id !== id))
    if (activeTab === 'cronjobs') setCronjobs(cronjobs.filter(c => c.id !== id))
    if (activeTab === 'functions') setFunctions(functions.filter(f => f.id !== id))
    if (activeTab === 'databases') setDatabases(databases.filter(d => d.id !== id))
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    alert('Copied to clipboard!')
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Backend Automation & Webhooks</h1>
        <p className="text-blue-200">Create webhooks, cron jobs, and custom backend functions to automate your workflows</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-blue-500/20 overflow-x-auto">
        <button
          onClick={() => setActiveTab('webhooks')}
          className={`px-6 py-3 font-semibold transition-all whitespace-nowrap ${
            activeTab === 'webhooks'
              ? 'text-blue-400 border-b-2 border-blue-500'
              : 'text-blue-300 hover:text-blue-200'
          }`}
        >
          <Zap className="inline mr-2" size={18} />
          Webhooks ({webhooks.length})
        </button>
        <button
          onClick={() => setActiveTab('cronjobs')}
          className={`px-6 py-3 font-semibold transition-all whitespace-nowrap ${
            activeTab === 'cronjobs'
              ? 'text-blue-400 border-b-2 border-blue-500'
              : 'text-blue-300 hover:text-blue-200'
          }`}
        >
          <Clock className="inline mr-2" size={18} />
          Cron Jobs ({cronjobs.length})
        </button>
        <button
          onClick={() => setActiveTab('functions')}
          className={`px-6 py-3 font-semibold transition-all whitespace-nowrap ${
            activeTab === 'functions'
              ? 'text-blue-400 border-b-2 border-blue-500'
              : 'text-blue-300 hover:text-blue-200'
          }`}
        >
          <Code className="inline mr-2" size={18} />
          Functions ({functions.length})
        </button>
        <button
          onClick={() => setActiveTab('databases')}
          className={`px-6 py-3 font-semibold transition-all whitespace-nowrap ${
            activeTab === 'databases'
              ? 'text-blue-400 border-b-2 border-blue-500'
              : 'text-blue-300 hover:text-blue-200'
          }`}
        >
          <Database className="inline mr-2" size={18} />
          Databases ({databases.length})
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-6">
          <p className="text-blue-300/70 text-sm mb-1">Total Webhooks</p>
          <p className="text-3xl font-bold text-blue-400">{webhooks.length}</p>
          <p className="text-xs text-blue-300 mt-2">Active and running</p>
        </div>

        <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-lg border border-purple-500/30 p-6">
          <p className="text-purple-300/70 text-sm mb-1">Scheduled Jobs</p>
          <p className="text-3xl font-bold text-purple-400">{cronjobs.length}</p>
          <p className="text-xs text-purple-300 mt-2">Cron jobs running</p>
        </div>

        <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 rounded-lg border border-green-500/30 p-6">
          <p className="text-green-300/70 text-sm mb-1">Functions</p>
          <p className="text-3xl font-bold text-green-400">{functions.length}</p>
          <p className="text-xs text-green-300 mt-2">Custom functions</p>
        </div>

        <div className="bg-gradient-to-br from-orange-600/20 to-red-600/20 rounded-lg border border-orange-500/30 p-6">
          <p className="text-orange-300/70 text-sm mb-1">Databases</p>
          <p className="text-3xl font-bold text-orange-400">{databases.length}</p>
          <p className="text-xs text-orange-300 mt-2">Connected databases</p>
        </div>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-6">
          <h2 className="text-xl font-bold text-white mb-4">
            {editingId ? 'Edit' : 'Add New'} {activeTab === 'webhooks' ? 'Webhook' : activeTab === 'cronjobs' ? 'Cron Job' : activeTab === 'functions' ? 'Function' : 'Database'}
          </h2>

          <div className="space-y-4">
            <div>
              <label className="text-blue-300 font-semibold text-sm mb-2 block">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Daily Report Generator"
                className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
              />
            </div>

            {activeTab === 'webhooks' && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-blue-300 font-semibold text-sm mb-2 block">Webhook URL</label>
                    <input
                      type="url"
                      value={formData.url}
                      onChange={(e) => setFormData({...formData, url: e.target.value})}
                      placeholder="https://api.example.com/webhook"
                      className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
                    />
                  </div>
                  <div>
                    <label className="text-blue-300 font-semibold text-sm mb-2 block">Method</label>
                    <select
                      value={formData.method}
                      onChange={(e) => setFormData({...formData, method: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white focus:outline-none focus:border-blue-500/60"
                    >
                      <option>GET</option>
                      <option>POST</option>
                      <option>PUT</option>
                      <option>DELETE</option>
                      <option>PATCH</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'cronjobs' && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-blue-300 font-semibold text-sm mb-2 block">Schedule (Cron)</label>
                    <input
                      type="text"
                      value={formData.schedule}
                      onChange={(e) => setFormData({...formData, schedule: e.target.value})}
                      placeholder="0 8 * * * (8 AM daily)"
                      className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
                    />
                  </div>
                  <div>
                    <label className="text-blue-300 font-semibold text-sm mb-2 block">Function Name</label>
                    <input
                      type="text"
                      value={formData.function}
                      onChange={(e) => setFormData({...formData, function: e.target.value})}
                      placeholder="e.g., generateDailyReport"
                      className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
                    />
                  </div>
                </div>
              </>
            )}

            {activeTab === 'functions' && (
              <>
                <div>
                  <label className="text-blue-300 font-semibold text-sm mb-2 block">Trigger Type</label>
                  <select
                    value={formData.trigger}
                    onChange={(e) => setFormData({...formData, trigger: e.target.value})}
                    className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white focus:outline-none focus:border-blue-500/60"
                  >
                    <option>Webhook</option>
                    <option>Cron</option>
                    <option>Webhook + Cron</option>
                    <option>Manual</option>
                  </select>
                </div>
                <div>
                  <label className="text-blue-300 font-semibold text-sm mb-2 block">Code</label>
                  <textarea
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    placeholder="async function myFunction() { /* your code here */ }"
                    rows="6"
                    className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60 font-mono text-sm"
                  />
                </div>
              </>
            )}

            {activeTab === 'databases' && (
              <>
                <div>
                  <label className="text-blue-300 font-semibold text-sm mb-2 block">Database Type</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {dbTypes.map(db => (
                      <button
                        key={db.name}
                        onClick={() => setFormData({...formData, dbType: db.name, dbPort: db.port || ''})}
                        className={`p-3 rounded-lg border-2 transition-all text-center ${
                          formData.dbType === db.name
                            ? 'border-blue-500 bg-blue-600/30 text-white'
                            : 'border-blue-500/30 bg-slate-800/50 text-blue-300 hover:border-blue-500'
                        }`}
                      >
                        <div className="text-2xl mb-1">{db.icon}</div>
                        <div className="text-xs font-semibold">{db.name}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {formData.dbType === 'Firebase' ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-blue-300 font-semibold text-sm mb-2 block">Project ID</label>
                        <input
                          type="text"
                          value={formData.dbProjectId}
                          onChange={(e) => setFormData({...formData, dbProjectId: e.target.value})}
                          placeholder="your-firebase-project"
                          className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
                        />
                      </div>
                      <div>
                        <label className="text-blue-300 font-semibold text-sm mb-2 block">Region</label>
                        <input
                          type="text"
                          value={formData.dbRegion}
                          onChange={(e) => setFormData({...formData, dbRegion: e.target.value})}
                          placeholder="us-central1"
                          className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-blue-300 font-semibold text-sm mb-2 block">Host</label>
                        <input
                          type="text"
                          value={formData.dbHost}
                          onChange={(e) => setFormData({...formData, dbHost: e.target.value})}
                          placeholder="localhost or db.example.com"
                          className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
                        />
                      </div>
                      <div>
                        <label className="text-blue-300 font-semibold text-sm mb-2 block">Port</label>
                        <input
                          type="number"
                          value={formData.dbPort}
                          onChange={(e) => setFormData({...formData, dbPort: e.target.value})}
                          placeholder={dbTypes.find(d => d.name === formData.dbType)?.port}
                          className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-blue-300 font-semibold text-sm mb-2 block">Database Name</label>
                        <input
                          type="text"
                          value={formData.dbName}
                          onChange={(e) => setFormData({...formData, dbName: e.target.value})}
                          placeholder="database_name"
                          className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
                        />
                      </div>
                      <div>
                        <label className="text-blue-300 font-semibold text-sm mb-2 block">Username</label>
                        <input
                          type="text"
                          value={formData.dbUser}
                          onChange={(e) => setFormData({...formData, dbUser: e.target.value})}
                          placeholder="username"
                          className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-blue-300 font-semibold text-sm mb-2 block">Password</label>
                      <input
                        type="password"
                        value={formData.dbPassword}
                        onChange={(e) => setFormData({...formData, dbPassword: e.target.value})}
                        placeholder="password"
                        className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
                      />
                    </div>
                  </>
                )}
              </>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleAddItem}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-green-500/50 transition-all"
              >
                {editingId ? 'Update' : 'Create'}
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false)
                  setEditingId(null)
                  setFormData({ name: '', url: '', method: 'POST', schedule: '', function: '', code: '', trigger: 'Webhook' })
                }}
                className="flex-1 px-4 py-2 bg-slate-600/30 text-slate-300 rounded-lg font-semibold hover:bg-slate-600/50 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {!showAddForm && (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-blue-500/50 transition-all flex items-center justify-center space-x-2"
        >
          <Plus size={20} />
          <span>Add {activeTab === 'webhooks' ? 'Webhook' : activeTab === 'cronjobs' ? 'Cron Job' : 'Function'}</span>
        </button>
      )}

      {/* Content by Tab */}
      <div className="space-y-4">
        {activeTab === 'webhooks' && webhooks.map(webhook => (
          <div key={webhook.id} className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">{webhook.name}</h3>
                <p className="text-sm text-blue-300 mt-1">{webhook.method} - {webhook.url}</p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="px-3 py-1 bg-green-500/30 text-green-300 text-xs font-bold rounded-full flex items-center space-x-1">
                  <CheckCircle size={12} />
                  <span>{webhook.status}</span>
                </span>
                <button onClick={() => copyToClipboard(webhook.url)} className="p-2 text-blue-300 hover:bg-blue-600/20 rounded-lg"><Copy size={18} /></button>
                <button onClick={() => handleDelete(webhook.id)} className="p-2 text-red-300 hover:bg-red-600/20 rounded-lg"><Trash2 size={18} /></button>
              </div>
            </div>
            <div className="bg-slate-900/30 rounded p-2 text-xs text-blue-200">
              Last triggered: {webhook.lastTriggered} • Total triggers: {webhook.triggers}
            </div>
          </div>
        ))}

        {activeTab === 'cronjobs' && cronjobs.map(job => (
          <div key={job.id} className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">{job.name}</h3>
                <p className="text-sm text-blue-300 mt-1">Schedule: {job.schedule} • Function: {job.function}</p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="px-3 py-1 bg-green-500/30 text-green-300 text-xs font-bold rounded-full flex items-center space-x-1">
                  <CheckCircle size={12} />
                  <span>{job.status}</span>
                </span>
                <button onClick={() => handleDelete(job.id)} className="p-2 text-red-300 hover:bg-red-600/20 rounded-lg"><Trash2 size={18} /></button>
              </div>
            </div>
            <div className="bg-slate-900/30 rounded p-2 text-xs text-blue-200 space-y-1">
              <div>Next run: {job.nextRun}</div>
              <div>Last run: {job.lastRun} • Total runs: {job.runs}</div>
            </div>
          </div>
        ))}

        {activeTab === 'functions' && functions.map(fn => (
          <div key={fn.id} className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">{fn.name}</h3>
                <p className="text-sm text-blue-300 mt-1">Trigger: {fn.trigger}</p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="px-3 py-1 bg-green-500/30 text-green-300 text-xs font-bold rounded-full flex items-center space-x-1">
                  <CheckCircle size={12} />
                  <span>{fn.status}</span>
                </span>
                <button className="p-2 text-blue-300 hover:bg-blue-600/20 rounded-lg"><Play size={18} /></button>
                <button onClick={() => handleDelete(fn.id)} className="p-2 text-red-300 hover:bg-red-600/20 rounded-lg"><Trash2 size={18} /></button>
              </div>
            </div>
            <div className="bg-slate-900/50 rounded p-3 mb-2 max-h-24 overflow-y-auto">
              <code className="text-xs text-cyan-300 font-mono whitespace-pre-wrap break-words">{fn.code}</code>
            </div>
            <div className="bg-slate-900/30 rounded p-2 text-xs text-blue-200">
              Last executed: {fn.lastExecuted} • Total executions: {fn.executions}
            </div>
          </div>
        ))}

        {activeTab === 'databases' && databases.map(db => (
          <div key={db.id} className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">{db.name}</h3>
                <p className="text-sm text-blue-300 mt-1">{db.type} Database</p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="px-3 py-1 bg-green-500/30 text-green-300 text-xs font-bold rounded-full flex items-center space-x-1">
                  <CheckCircle size={12} />
                  <span>{db.status}</span>
                </span>
                <button onClick={() => handleDelete(db.id)} className="p-2 text-red-300 hover:bg-red-600/20 rounded-lg"><Trash2 size={18} /></button>
              </div>
            </div>
            <div className="bg-slate-900/30 rounded p-3 space-y-2 text-xs text-blue-200">
              <div className="flex justify-between">
                <span>Type:</span>
                <span className="font-semibold">{db.type}</span>
              </div>
              {db.projectId ? (
                <>
                  <div className="flex justify-between">
                    <span>Project ID:</span>
                    <span className="font-mono">{db.projectId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Region:</span>
                    <span className="font-mono">{db.region}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between">
                    <span>Host:</span>
                    <span className="font-mono">{db.host}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Port:</span>
                    <span className="font-mono">{db.port}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Database:</span>
                    <span className="font-mono">{db.database}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between pt-2 border-t border-blue-500/20">
                <span>Last Connection:</span>
                <span>{db.lastConnection}</span>
              </div>
              <div className="flex justify-between">
                <span>Collections/Tables:</span>
                <span className="font-semibold">{db.collections}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {databases.length === 0 && !showAddForm && activeTab === 'databases' && (
        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-12 text-center">
          <Database size={48} className="text-blue-300 mx-auto mb-4 opacity-50" />
          <p className="text-blue-200 text-lg">No databases configured</p>
          <p className="text-blue-300/70 mt-2">Add your first database connection</p>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-blue-500/10 rounded-lg border border-blue-500/30 p-6">
        <h3 className="text-lg font-bold text-blue-300 mb-3">📚 How to Use</h3>
        <div className="space-y-3 text-sm text-blue-200">
          <div>
            <p className="font-semibold text-blue-300">Webhooks:</p>
            <p>Create endpoints that respond to HTTP requests from external services. Use POST/PUT to receive data and trigger actions.</p>
          </div>
          <div>
            <p className="font-semibold text-blue-300">Cron Jobs:</p>
            <p>Schedule tasks to run automatically at specific times. Use cron syntax (0 8 * * * = 8 AM daily)</p>
          </div>
          <div>
            <p className="font-semibold text-blue-300">Functions:</p>
            <p>Write custom JavaScript functions that can be triggered by webhooks, cron jobs, or manually. Use async/await for API calls.</p>
          </div>
          <div>
            <p className="font-semibold text-blue-300">Databases:</p>
            <p>Configure connections to any major database: PostgreSQL, MySQL, MariaDB, MongoDB, Firebase, SQL Server, Oracle, or Cassandra. Use these connections in your functions and webhooks.</p>
          </div>
        </div>
      </div>

      {/* Database Connection Strings Guide */}
      <div className="bg-purple-500/10 rounded-lg border border-purple-500/30 p-6">
        <h3 className="text-lg font-bold text-purple-300 mb-3">🔌 Connection String Examples for Agents</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-purple-200 font-mono">
          <div className="bg-slate-800/50 p-3 rounded border border-purple-500/20">
            <p className="text-purple-300 font-semibold mb-1">PostgreSQL</p>
            <code>postgresql://user:password@host:5432/database</code>
          </div>
          <div className="bg-slate-800/50 p-3 rounded border border-purple-500/20">
            <p className="text-purple-300 font-semibold mb-1">MySQL/MariaDB</p>
            <code>mysql://user:password@host:3306/database</code>
          </div>
          <div className="bg-slate-800/50 p-3 rounded border border-purple-500/20">
            <p className="text-purple-300 font-semibold mb-1">MongoDB</p>
            <code>mongodb://user:password@host:27017/database</code>
          </div>
          <div className="bg-slate-800/50 p-3 rounded border border-purple-500/20">
            <p className="text-purple-300 font-semibold mb-1">Firebase</p>
            <code>projects/projectId/databases/(default)</code>
          </div>
          <div className="bg-slate-800/50 p-3 rounded border border-purple-500/20">
            <p className="text-purple-300 font-semibold mb-1">SQL Server</p>
            <code>mssql://user:password@host:1433/database</code>
          </div>
          <div className="bg-slate-800/50 p-3 rounded border border-purple-500/20">
            <p className="text-purple-300 font-semibold mb-1">Oracle</p>
            <code>oracle://user:password@host:1521/database</code>
          </div>
        </div>
      </div>
    </div>
  )
}
